import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { formatDate } from 'utils/helpers'
import { AuditChangedField } from './auditLogChangesModal'

// Module (top) + Action (bottom, coloured)
const ModuleActionCell = ({ data }: ICellRendererParams) => (
  <div className="flex flex-col leading-tight">
    <span className="text-sm font-medium capitalize text-gray-800">
      {data?.module}
    </span>
    <span className={`text-xs text-gray-400`}>Action: {data?.action}</span>
  </div>
)

const UserInfoCell = ({ data }: ICellRendererParams) => (
  <div className="flex flex-col py-1 leading-tight">
    <span className="text-sm font-medium text-gray-800">
      {data?.user?.name ?? '—'}
    </span>
    <span className="text-xs text-gray-400">
      Emp ID: {data?.user?.employeeId ?? ''}
    </span>
  </div>
)

// Entity label (top) + Entity ID (bottom, muted)
const EntityCell = ({ data }: ICellRendererParams) => (
  <div className="flex flex-col justify-center py-1 leading-tight">
    <span className="text-sm font-medium text-gray-800">
      {data?.entityLabel ?? '—'}
    </span>
    <span className="text-xs text-gray-400">{data?.entityId ?? ''}</span>
  </div>
)

// Changed field name pills
const ChangedFieldsCell = ({ value }: ICellRendererParams) => {
  const changes: AuditChangedField[] = value ?? []

  if (!changes.length) return <span className="text-gray-400">—</span>

  return (
    <div className="flex flex-wrap gap-1 py-1">
      {changes.map((c) => (
        <span
          key={c.field}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
        >
          {c.field}
        </span>
      ))}
    </div>
  )
}

export const getAdminAuditLogColumns = (
  page: number,
  onView: (params: ICellRendererParams) => void,
): ColDef[] => [
  {
    headerName: 'ID',
    field: 'uuid',
    valueFormatter: (params: ValueFormatterParams) =>
      String((params.node?.rowIndex as number) + 1 + page),
    maxWidth: 80,
    sortable: false,
  },
  {
    headerName: 'Module',
    field: 'module',
    minWidth: 200,
    sortable: false,
    cellRenderer: (params: ICellRendererParams) => (
      <ModuleActionCell {...params} />
    ),
  },
  {
    headerName: 'Entity',
    field: 'entityLabel',
    minWidth: 220,
    sortable: false,
    cellRenderer: (params: ICellRendererParams) => <EntityCell {...params} />,
  },
  {
    headerName: 'Changed Fields',
    field: 'changes',
    minWidth: 300,
    sortable: false,
    wrapText: true,
    autoHeight: true,
    cellRenderer: (params: ICellRendererParams) => (
      <ChangedFieldsCell {...params} />
    ),
  },
  {
    headerName: 'Performed By',
    field: 'user.name',
    minWidth: 180,
    sortable: false,
    cellRenderer: (params: ICellRendererParams) => <UserInfoCell {...params} />,
  },
  {
    headerName: 'Date',
    field: 'createdAt',
    minWidth: 180,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value, { includeTime: true }) : '—',
    sort: 'desc',
  },
  {
    headerName: 'Action',
    field: '_id',
    maxWidth: 90,
    sortable: false,
    pinned: 'right',
    cellRenderer: (params: ICellRendererParams) => (
      <button
        onClick={() => onView(params)}
        className="rounded-md bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600
          transition-colors hover:bg-gray-200 hover:text-gray-800"
      >
        View
      </button>
    ),
  },
]
